import React, { useState, useMemo } from "react";
import {
  Calendar as BigCalendar,
  dateFnsLocalizer,
  Views,
} from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import ReactCalendar from "react-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-calendar/dist/Calendar.css";
import "./PafCalendar.css";
import { YearlyView } from "./component"; // ✅ custom yearly view

const locales = {
  "en-US": require("date-fns/locale/en-US"),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

// 🔹 Events for BigCalendar
const events = [
  {
    title: "Annual PAF Event",
    start: new Date(2025, 7, 27, 10, 0),
    end: new Date(2025, 7, 27, 12, 0),
    type: "annual",
  },
  {
    title: "Annual Holiday",
    start: new Date(2025, 7, 25),
    end: new Date(2025, 7, 25),
    type: "annual",
  },
  {
    title: "Annual Strategy Meeting",
    start: new Date(2025, 8, 5, 11, 0),
    end: new Date(2025, 8, 5, 13, 0),
    type: "annual",
  },
  {
    title: "Annual Founders Day",
    start: new Date(2025, 11, 15, 9, 0),
    end: new Date(2025, 11, 15, 17, 0),
    type: "annual",
  },
  {
    title: "Quarterly Team Meeting",
    start: new Date(2025, 7, 29, 9, 0),
    end: new Date(2025, 7, 29, 10, 0),
    type: "quarterly",
  },
  {
    title: "Quarterly Client Presentation",
    start: new Date(2025, 7, 29, 14, 0),
    end: new Date(2025, 7, 29, 15, 0),
    type: "quarterly",
  },
  {
    title: "Quarterly Review Session",
    start: new Date(2025, 9, 3, 15, 0),
    end: new Date(2025, 9, 3, 16, 30),
    type: "quarterly",
  },
  {
    title: "Quarterly Financial Report",
    start: new Date(2025, 10, 20, 10, 0),
    end: new Date(2025, 10, 20, 11, 30),
    type: "quarterly",
  },
];

// 🔹 Events for Yearly View
const yearlyEvents = [
  { date: "2025-08-15", title: "IMS Project Deadline" },
  { date: "2025-09-05", title: "Project Deadline" },
  { date: "2025-12-25", title: "Christmas" },
  { date: "2025-11-12", title: "Team Meeting" },
  { date: "2025-10-10", title: "Thanksgiving" },
];

const PafCalendar = () => {
  const [currentView, setCurrentView] = useState(Views.MONTH);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: sidebarOpen ? "280px 1fr" : "1fr",
        height: "90vh",
        background: "#fff",
        border: "1px solid #DADCE0",
        borderRadius: "8px",
        padding: "10px 0px",
        overflowY: "auto",
      }}
    >
      {/* 🔹 Sidebar Calendar */}
      {sidebarOpen && (
        <div style={{ padding: "10px", position: "relative" }}>
          <ReactCalendar
            onChange={setSelectedDate}
            value={selectedDate}
            locale="en-GB"
            prev2Label={null}
            next2Label={null}
            showNeighboringMonth={false}
            formatShortWeekday={(locale, date) =>
              date.toLocaleDateString(locale, { weekday: "short" }).slice(0, 1)
            }
            tileClassName={({ date, view }) => {
              if (view !== "month") return null;
              const d = new Date(date);
              d.setHours(0, 0, 0, 0);

              // 🔹 Highlight rules
              if (d.getTime() === today.getTime()) return "is-today";
              if (d < today) return "past-date";
              if (date.getDay() === 6 || date.getDay() === 0) return "weekend";

              // highlight yearly events
              const eventMatch = yearlyEvents.find(
                (e) => new Date(e.date).getTime() === d.getTime()
              );
              if (eventMatch) return "highlight-event";

              return null;
            }}
            tileDisabled={({ date, view }) => {
              if (view !== "month") return false;
              const d = new Date(date);
              d.setHours(0, 0, 0, 0);
              return d < today;
            }}
          />

          <div style={{ marginTop: 20, textAlign: "center" }}>
            <h4>Upcoming PAF's</h4>
            <p>🎉 No upcoming PAF's</p>
          </div>
        </div>
      )}

      {/* 🔹 Main Big Calendar / Yearly View */}
      <div style={{ position: "relative" }}>
        <div
          style={{
            paddingBottom: "10px",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            borderLeft: "1px solid #DADCE0",
          }}
        >
          <div
            style={{
              fontWeight: "bold",
              fontSize: "16px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              paddingLeft: "20px",
            }}
          >
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="bg-transparent border-0 cursor-pointer p-0 m-0"
            >
              <img src="/assets/images/calendar-bar-close.svg" alt="" />
            </button>
            {/* 🔹 Conditional heading */}
            {currentView === "year"
              ? localizer.format(new Date(), "yyyy") // show year only
              : localizer.format(new Date(), "MMMM dd, yyyy")}
          </div>

          <select
            value={currentView}
            onChange={(e) => setCurrentView(e.target.value)}
          >
            <option value={Views.DAY}>Day</option>
            <option value={Views.WEEK}>Week</option>
            <option value={Views.MONTH}>Month</option>
            <option value="year">Year</option> {/* ✅ Added Year option */}
          </select>
        </div>

        {/* 🔹 Conditional rendering for Yearly View */}
        {currentView === "year" ? (
          <YearlyView
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            events={yearlyEvents} // ✅ fixed prop
          />
        ) : (
          <BigCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: "100%" }}
            views={{ day: true, week: true, month: true }}
            view={currentView}
            onView={(view) => setCurrentView(view)}
            toolbar={false}
            date={selectedDate}
            onNavigate={(date) => setSelectedDate(date)}
            eventPropGetter={(event) => {
              let backgroundColor = "";
              let border = "";
              let color = "";

              if (event.type === "annual") {
                backgroundColor = "#D6C8FF";
                border = "1px solid #A384FF";
                color = "#341D76";
              }

              if (event.type === "quarterly") {
                backgroundColor = "#BDFFDB";
                border = "1px solid #8FDCB2";
                color = "#2C5A41";
              }

              return {
                style: {
                  backgroundColor,
                  border,
                  borderRadius: "6px",
                  color,
                  fontWeight: 400,
                  fontSize: "10px",
                  padding: "2px 6px",
                },
              };
            }}
            components={{
              timeGutterHeader: () => null,
              timeGutterWrapper: () => null,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default PafCalendar;
